import type { ReactNode } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router";
import { ArrowLeft, ArrowRight, Compass, RefreshCw, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/cn";
import { DigiSubsMark } from "@/components/brand/logos";
import { Button, ButtonLink } from "@/components/ui";

// System pages: not found (also shown for the platform admin area to everybody else) and the route error boundary,
// in the app card language with a clear way back.

function SystemCard({ icon, eyebrow, title, description, actions, tone = "blue" }: { icon: ReactNode; eyebrow: string; title: string; description: ReactNode; actions: ReactNode; tone?: "blue" | "red" }) {
  return (
    <div className="card relative w-full max-w-lg overflow-hidden px-6 py-9 text-center sm:px-10">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(ellipse_60%_100%_at_50%_0%,rgb(47_107_255/0.18),transparent_75%)]" />
      <span
        className={cn(
          "relative mx-auto flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br [&>svg]:size-6.5",
          tone === "red" ? "from-[#7f1d1d] to-[#dc2626] text-[#fecaca] shadow-[0_10px_26px_-10px_rgb(239_68_68/0.7)]" : "from-[#1d4ed8] to-[#2f6bff] text-white shadow-[0_10px_26px_-10px_rgb(47_107_255/0.9)]",
        )}
      >
        {icon}
      </span>
      <p className={cn("relative mt-5 text-[12px] font-semibold uppercase tracking-[0.12em]", tone === "red" ? "text-[#f87171]" : "text-primary-400")}>{eyebrow}</p>
      <h1 className="relative mt-1.5 text-[24px] font-bold tracking-tight text-white sm:text-[28px]">{title}</h1>
      <div className="relative mx-auto mt-2 max-w-sm text-[13.5px] leading-relaxed text-muted">{description}</div>
      <div className="relative mt-7 flex flex-wrap items-center justify-center gap-2">{actions}</div>
    </div>
  );
}

export function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4 sm:p-6">
      <SystemCard
        icon={<Compass />}
        eyebrow="Erreur 404"
        title="Page introuvable"
        description="Cette page n'existe pas, a été déplacée, ou votre compte n'y a pas accès."
        actions={
          <>
            <Button variant="secondary" leftIcon={<ArrowLeft className="size-4" />} onClick={() => navigate(-1)}>
              Page précédente
            </Button>
            <ButtonLink to="/" rightIcon={<ArrowRight className="size-4" />}>
              Retour à l'accueil
            </ButtonLink>
          </>
        }
      />
    </div>
  );
}

export function RouteErrorPage() {
  const error = useRouteError();
  const message = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : error instanceof Error ? error.message : "Erreur inconnue";
  const chunkError = /Failed to fetch dynamically imported module|Importing a module script failed/i.test(message);
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-canvas p-4 sm:p-6">
      <div className="flex items-center gap-2.5">
        <DigiSubsMark size={32} />
        <span className="text-[17px] font-bold text-white">DigiSubs</span>
      </div>
      <SystemCard
        icon={chunkError ? <RefreshCw /> : <TriangleAlert />}
        tone={chunkError ? "blue" : "red"}
        eyebrow={chunkError ? "Mise à jour" : "Erreur"}
        title={chunkError ? "Nouvelle version disponible" : "Une erreur est survenue"}
        description={chunkError ? "La plateforme a été mise à jour. Rechargez la page pour continuer." : <span className="break-words">{message}</span>}
        actions={
          <>
            {!chunkError ? (
              <Button variant="secondary" leftIcon={<ArrowLeft className="size-4" />} onClick={() => window.location.assign("/")}>
                Retour à l'accueil
              </Button>
            ) : null}
            <Button leftIcon={<RefreshCw className="size-4" />} onClick={() => window.location.reload()}>
              Recharger
            </Button>
          </>
        }
      />
    </div>
  );
}
